<template>
  <div class="form-group pin-location-field">
    <label :for="inputId">{{ label }}</label>

    <div v-if="savedUrl && !editorOpen" class="pin-location-saved">
      <a
        href="#"
        class="pin-location-view-link"
        @click.prevent="openSavedLocation"
      >
        <i class="fa-solid fa-map-location-dot" aria-hidden="true" />
        View pinned location
      </a>
    </div>

    <button
      v-if="!editorOpen"
      type="button"
      class="pin-location-update-link"
      @click="startUpdate"
    >
      <i class="fa-solid fa-location-arrow" aria-hidden="true" />
      {{ savedUrl ? "Update Location" : "Add Location" }}
    </button>

    <div v-else class="pin-location-paste-panel">
      <p class="pin-location-hint">
        Google Maps opens in a new tab. Use <strong>Share</strong>, copy the link, then paste it here.
      </p>
      <input
        :id="inputId"
        v-model.trim="draft"
        type="url"
        inputmode="url"
        autocomplete="off"
        class="pin-location-input"
        :class="{ error: Boolean(errorMessage) }"
        placeholder="https://maps.app.goo.gl/…"
        @input="onDraftInput"
      />
      <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
      <div class="pin-location-actions">
        <button type="button" class="pin-location-action-link" @click="openGoogleMaps">
          <i class="fa-brands fa-google" aria-hidden="true" />
          Open Google Maps again
        </button>
        <button type="button" class="pin-location-action-link pin-location-action-link--muted" @click="cancelUpdate">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  buildGoogleMapsSearchUrl,
  isValidGoogleMapsUrl,
  sanitizeGoogleMapsUrl,
  resolvePinLocationForSave,
} from "@/utils/googleMapsUrl";

export default {
  name: "PinLocationEditor",

  props: {
    savedUrl: {
      type: String,
      default: "",
    },
    venueQuery: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "Pin / map link (optional)",
    },
    inputId: {
      type: String,
      default: "homeGroundPin",
    },
    errorMessage: {
      type: String,
      default: "",
    },
  },

  emits: ["clear-error"],

  data() {
    return {
      editorOpen: false,
      draft: "",
    };
  },

  watch: {
    savedUrl() {
      this.resetEditor();
    },
  },

  methods: {
    resetEditor() {
      this.editorOpen = false;
      this.draft = "";
    },

    onDraftInput() {
      this.$emit("clear-error");
    },

    startUpdate() {
      this.editorOpen = true;
      this.draft = "";
      this.$emit("clear-error");
      this.openGoogleMaps();
    },

    cancelUpdate() {
      this.resetEditor();
      this.$emit("clear-error");
    },

    openGoogleMaps() {
      window.open(buildGoogleMapsSearchUrl(this.venueQuery), "_blank", "noopener,noreferrer");
    },

    openSavedLocation() {
      const url = (this.savedUrl || "").trim();
      if (!isValidGoogleMapsUrl(url)) return;
      const safe = sanitizeGoogleMapsUrl(url);
      if (safe) {
        window.open(safe, "_blank", "noopener,noreferrer");
      }
    },

    resolveForSave() {
      return resolvePinLocationForSave({
        editorOpen: this.editorOpen,
        draft: this.draft,
        savedUrl: this.savedUrl,
      });
    },
  },
};
</script>

<style scoped>
.pin-location-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pin-location-saved {
  margin: 0;
}

.pin-location-view-link,
.pin-location-update-link,
.pin-location-action-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.pin-location-view-link {
  color: var(--border-color-hover, #2563eb);
}

.pin-location-view-link:hover {
  text-decoration: underline;
}

.pin-location-update-link {
  color: var(--text-color1, #0f172a);
}

.pin-location-update-link:hover,
.pin-location-action-link:hover {
  text-decoration: underline;
}

.pin-location-paste-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pin-location-hint {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--text-color2, #64748b);
}

.pin-location-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color-hover1, #cbd5e1);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--background-color1, #fff);
  color: var(--text-color1, #0f172a);
}

.pin-location-input.error {
  border-color: #dc2626;
}

.pin-location-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
}

.pin-location-action-link {
  color: var(--border-color-hover, #2563eb);
  font-size: 0.84rem;
}

.pin-location-action-link--muted {
  color: var(--text-color2, #64748b);
}
</style>
